import { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();

type TurnstileWidgetId = string;

interface TurnstileApi {
    render: (
        container: HTMLElement,
        options: {
            sitekey: string;
            theme: 'auto';
            callback: (token: string) => void;
            'expired-callback': () => void;
            'error-callback': () => void;
        }
    ) => TurnstileWidgetId;
    remove: (widgetId: TurnstileWidgetId) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let turnstileScript: Promise<TurnstileApi> | undefined;

function loadTurnstileScript(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileScript) return turnstileScript;

    const scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
        const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
        const script = existing ?? document.createElement('script');

        const onLoad = () => {
            if (window.turnstile) {
                resolve(window.turnstile);
            } else {
                reject(new Error('Turnstile script loaded without its API'));
            }
        };
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', () => reject(new Error('Could not load Turnstile')), {
            once: true,
        });

        if (!existing) {
            script.id = TURNSTILE_SCRIPT_ID;
            script.src = TURNSTILE_SCRIPT_URL;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    });

    turnstileScript = scriptPromise.catch((error: unknown) => {
        // A failed script element cannot recover; remove it and allow the next
        // widget mount to try again instead of requiring a full-page reload.
        document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
        turnstileScript = undefined;
        throw error;
    });

    return turnstileScript;
}

interface TurnstileCaptchaProps {
    onTokenChange: (token: string | null) => void;
    refreshKey?: number;
}

/**
 * Explicit Cloudflare Turnstile widget for public self-serve actions.
 *
 * The site key is public by design. The token is deliberately short-lived and
 * only lets the caller submit once; the server remains responsible for
 * Siteverify validation before performing any protected action.
 */
function TurnstileWidget({ onTokenChange }: Pick<TurnstileCaptchaProps, 'onTokenChange'>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<TurnstileWidgetId | undefined>(undefined);

    useEffect(() => {
        if (!containerRef.current) return;

        let removed = false;
        onTokenChange(null);

        void loadTurnstileScript()
            .then((turnstile) => {
                if (removed || !containerRef.current) return;
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY as string,
                    theme: 'auto',
                    callback: (token) => onTokenChange(token),
                    'expired-callback': () => onTokenChange(null),
                    'error-callback': () => onTokenChange(null),
                });
            })
            .catch(() => onTokenChange(null));

        return () => {
            removed = true;
            if (widgetIdRef.current) {
                window.turnstile?.remove(widgetIdRef.current);
                widgetIdRef.current = undefined;
            }
        };
    }, [onTokenChange]);

    return <div ref={containerRef} />;
}

export function TurnstileCaptcha({ onTokenChange, refreshKey = 0 }: TurnstileCaptchaProps) {
    if (!TURNSTILE_SITE_KEY) {
        return (
            <p role="alert" className="text-sm text-destructive">
                Sign-up protection is not configured. Please try again later.
            </p>
        );
    }

    return <TurnstileWidget key={refreshKey} onTokenChange={onTokenChange} />;
}
